// shared-lib/cabinet-runs/priceList.js
//
// A cabinet vendor's DATED price list (design 2026-10-05, D1/D7): one record
// per edition in the shared Firestore collection `cabinetPriceLists`, keyed
// `<familyId>--<effectiveDate>`, holding the manufacturer's cost of every SKU
// in each colour (`prices[SKU][CODE]`, dollars). Shared catalog data — every
// contractor pays the same list — and never committed to the repo.
//
// The CURRENT edition is the latest one in effect on a date; older editions
// stay for history. Pure.

const PRICE_LIST_COLLECTION = 'cabinetPriceLists';
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const CODE = /^[A-Z]{1,4}$/;

/** The document id of one edition. */
function editionId(familyId, effectiveDate) {
  return `${familyId}--${effectiveDate}`;
}

/** The problems with a price list, as strings; empty when it is sound. */
function validatePriceList(list) {
  if (!list || typeof list !== 'object') return ['price list is not an object'];
  const errs = [];
  if (typeof list.familyId !== 'string' || !list.familyId)
    errs.push('no familyId');
  if (!DATE.test(list.effectiveDate || ''))
    errs.push(`effectiveDate "${list.effectiveDate}" is not YYYY-MM-DD`);
  if (list.currency !== 'USD')
    errs.push(`currency must be USD (got ${JSON.stringify(list.currency)})`);
  const colours = Array.isArray(list.colours) ? list.colours : [];
  if (!colours.length) errs.push('no colours');
  for (const c of colours)
    if (!CODE.test(c))
      errs.push(`colour code "${c}" is not 1–4 capital letters`);
  if (new Set(colours).size !== colours.length)
    errs.push('colour codes must be unique');
  const prices = list.prices;
  if (!prices || typeof prices !== 'object' || !Object.keys(prices).length) {
    errs.push('no prices');
    return errs;
  }
  for (const [sku, row] of Object.entries(prices)) {
    for (const [code, value] of Object.entries(row || {})) {
      if (!colours.includes(code))
        errs.push(`${sku}: colour "${code}" is not in colours`);
      else if (!(Number.isFinite(value) && value > 0))
        errs.push(`${sku} ${code}: ${value} is not a positive price`);
    }
  }
  return errs;
}

/** The edition in effect on `today` ('YYYY-MM-DD'): the latest not after it. */
function currentEdition(editions, today) {
  let best = null;
  for (const e of editions || []) {
    if (!e || !DATE.test(e.effectiveDate || '') || e.effectiveDate > today)
      continue;
    if (!best || e.effectiveDate > best.effectiveDate) best = e;
  }
  return best;
}

/** One SKU's price in one colour, or null when the list has none. */
function priceFor(list, sku, code) {
  const v = list && list.prices && list.prices[sku] && list.prices[sku][code];
  return Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * A part's price with NO colour — one the list prices under a single colour
 * column although it has no colour of its own (Venus's RD roll-out trays,
 * HF only; user ruling 2026-10-25: a tray is cabinet-interior stock). The
 * price every colour that lists it agrees on, else null. Never copied into
 * every colour, which would be a data re-upload.
 */
function priceWithoutColour(list, sku) {
  const row = list && list.prices && list.prices[sku];
  if (!row) return null;
  const values = [
    ...new Set(Object.values(row).filter((v) => Number.isFinite(v) && v > 0)),
  ];
  return values.length === 1 ? values[0] : null;
}

module.exports = {
  PRICE_LIST_COLLECTION,
  editionId,
  validatePriceList,
  currentEdition,
  priceFor,
  priceWithoutColour,
};
